import { Calendar, Tag, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useTenantPath } from '@/lib/tenant'
import { isSoldOut } from '@/lib/training-list'

interface TrainingCardProps {
  id: string
  title: string
  description?: string | null
  trainingType: 'open' | 'assigned'
  category?: string | null
  tags?: string[]
  capacity?: number | null
  remainingSeats?: number | null
  standardDurationHours?: number | null
  isPublished?: boolean
}

export function TrainingCard({
  id,
  title,
  description,
  trainingType,
  category,
  tags,
  capacity,
  remainingSeats,
  standardDurationHours,
}: TrainingCardProps) {
  const tp = useTenantPath()

  return (
    <Link to={tp(`/trainings/${id}`)}>
      <Card className="hover:shadow-md transition-shadow h-full">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base line-clamp-2">{title}</CardTitle>
            <Badge variant={trainingType === 'open' ? 'secondary' : 'default'} className="shrink-0">
              {trainingType === 'open' ? '募集型' : 'アサイン型'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {description && (
            <p className="text-sm text-muted-foreground line-clamp-2">{description}</p>
          )}

          <div className="flex flex-wrap gap-1">
            {category && (
              <Badge variant="outline" className="text-xs">
                <Tag className="mr-1 h-3 w-3" />
                {category}
              </Badge>
            )}
            {tags?.map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>

          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            {remainingSeats != null && (
              <div className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                {isSoldOut(remainingSeats) ? (
                  <Badge variant="destructive" className="text-xs">
                    満席
                  </Badge>
                ) : (
                  <span>
                    残席 {remainingSeats}/{capacity}
                  </span>
                )}
              </div>
            )}
            {standardDurationHours != null && (
              <div className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                <span>{standardDurationHours}時間</span>
              </div>
            )}
          </div>

          <Button variant="outline" size="sm" className="w-full">
            詳細を見る
          </Button>
        </CardContent>
      </Card>
    </Link>
  )
}
